// src/components/Studio/ProductionCard.tsx
import { Link } from 'react-router-dom';
import { Clapperboard, MapPin, Users } from 'lucide-react';
import type { ProductionSummary } from '../../types/studio';
import { PANEL, PANEL_HOVER, STAGE_TONE } from './studioStyles';

const ProductionCard = ({ production }: { production: ProductionSummary }) => {
  const stageTone = STAGE_TONE[production.stage] ?? STAGE_TONE.DEVELOPMENT;

  return (
    <Link
      to={`/productions/${production.slug}`}
      className={`group block overflow-hidden ${PANEL} ${PANEL_HOVER} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400`}
    >
      <div className="relative aspect-[2/3] overflow-hidden bg-neutral-900">
        {production.posterUrl ? (
          <img
            src={production.posterUrl}
            alt=""
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Clapperboard className="h-10 w-10 text-neutral-700" aria-hidden="true" />
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 to-transparent" />

        <span
          className={`absolute left-3 top-3 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${stageTone}`}
        >
          {production.stageLabel}
        </span>

        {production.openForCasting && (
          <span className="absolute right-3 top-3 rounded-full border border-orange-500/40 bg-orange-500/20 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-orange-200">
            Casting
          </span>
        )}
      </div>

      <div className="p-4">
        <p className="text-[11px] uppercase tracking-wider text-neutral-500">
          {production.formatLabel}
          {production.productionYear ? ` · ${production.productionYear}` : ''}
        </p>

        <h3 className="mt-1.5 font-display text-base font-bold leading-snug text-white line-clamp-2">
          {production.title}
        </h3>

        {production.logline && (
          <p className="mt-2 text-sm leading-relaxed text-neutral-400 line-clamp-2">
            {production.logline}
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-neutral-500">
          {production.creditCount > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" aria-hidden="true" />
              {production.creditCount} credited
            </span>
          )}
          {production.shootLocation && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              {production.shootLocation}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductionCard;
